import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, ChevronRight, CircleAlert, Folder, LoaderCircle, MessageSquare, SquarePen } from 'lucide-react';
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import type { Attachment } from '../../api/types';
import { useAuth } from '../../auth/AuthProvider';
import { useChat } from '../../chat/ChatProvider';
import { Attachments, type AttachmentsHandle } from '../../components/Attachments';
import { ErrorState, LoadingState } from '../../components/AsyncState';
import { Avatar } from '../../components/Avatar';
import { MessageDateSeparator } from '../../components/MessageDateSeparator';
import { MessageFiles } from '../../components/MessageFiles';
import { Modal } from '../../components/Modal';
import { PersonCombobox } from '../../components/PersonCombobox';
import { RichTextEditor } from '../../components/RichTextEditor';
import { AssetBoard, type BoardSaveStatus } from '../board/AssetBoard';
import { FileBrowser } from '../files/FileBrowserModal';
import { formatDate } from '../../lib/status';
import { hasRichTextContent, renderRichText, stripInlineAttachments } from '../../lib/richText';
import '../board/board-shell.css';
import { BoardAvatarButton } from '../board/BoardAvatarButton';
import { AssetRelationsModal } from './AssetRelationsModal';

type AssetTab = 'board' | 'discussion' | 'files';

const boardStatusLabels: Record<BoardSaveStatus['kind'], string> = {
  loading: 'Загрузка доски…', saved: 'Сохранено', saving: 'Сохраняем…',
  dirty: 'Есть изменения', error: 'Не сохранено', conflict: 'Конфликт изменений',
};

export function AssetDetailsModal({
  assetId,
  onClose,
}: {
  assetId: number | null;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const chat = useChat();
  const { joinAsset, leaveAsset } = chat;
  const [tab, setTab] = useState<AssetTab>('board');
  const [boardStatus, setBoardStatus] = useState<BoardSaveStatus>({ kind: 'loading' });
  const boardBusy = useRef(false);
  const [boardCloseBlocked, setBoardCloseBlocked] = useState(false);
  const [message, setMessage] = useState('');
  const [receiverId, setReceiverId] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const attachmentControl = useRef<AttachmentsHandle>(null);
  const messageList = useRef<HTMLDivElement>(null);
  const historyRestore = useRef<{ assetId: number; height: number } | null>(null);
  const initiallyScrolled = useRef<number | null>(null);
  const discussionScroll = useRef<Record<number, { top: number; atBottom: boolean }>>({});
  const restoreDiscussion = useRef(false);
  const stickToBottom = useRef(true);
  const forceBottom = useRef(false);
  const fileUploadUnfinished = useRef(false);
  const [fileCloseBlocked, setFileCloseBlocked] = useState(false);
  const [relationsOpen, setRelationsOpen] = useState(false);

  const assetQuery = useQuery({
    queryKey: ['asset', assetId],
    queryFn: () => api.asset(assetId!),
    enabled: assetId !== null,
  });
  const projectId = assetQuery.data?.project?.id;
  const usersQuery = useQuery({
    queryKey: ['users', 'project', projectId],
    queryFn: () => api.users({ projectId }),
    enabled: projectId !== undefined,
  });

  useEffect(() => {
    setTab('board');
    setBoardStatus({ kind: 'loading' });
    boardBusy.current = false;
    setBoardCloseBlocked(false);
    setMessage('');
    setReceiverId('');
    setAttachments([]);
    initiallyScrolled.current = null;
    restoreDiscussion.current = false;
    stickToBottom.current = true;
    fileUploadUnfinished.current = false;
    setFileCloseBlocked(false);
    setRelationsOpen(false);
  }, [assetId]);
  useEffect(() => {
    if (assetId === null || tab !== 'discussion') return;
    joinAsset(assetId);
    return () => leaveAsset(assetId);
  }, [assetId, joinAsset, leaveAsset, tab]);

  const onBoardBusyChange = useCallback((busy: boolean) => {
    boardBusy.current = busy;
    if (!busy) setBoardCloseBlocked(false);
  }, []);
  const closeModal = () => {
    if (fileUploadUnfinished.current) { setFileCloseBlocked(true); return; }
    if (boardBusy.current) { setBoardCloseBlocked(true); return; }
    onClose();
  };
  const messages = useMemo(() => assetId === null ? [] : chat.assetMessages[assetId] ?? [], [assetId, chat.assetMessages]);
  const history = assetId === null ? undefined : chat.assetHistory[assetId];

  useLayoutEffect(() => {
    const node = messageList.current;
    if (!node || assetId === null || tab !== 'discussion') return;
    const restore = historyRestore.current;
    if (restore?.assetId === assetId && !history?.loading) {
      node.scrollTop = node.scrollHeight - restore.height;
      historyRestore.current = null;
      return;
    }
    if (restoreDiscussion.current && messages.length && !history?.loading) {
      const saved = discussionScroll.current[assetId];
      node.scrollTop = !saved || saved.atBottom ? node.scrollHeight : saved.top;
      stickToBottom.current = !saved || saved.atBottom;
      restoreDiscussion.current = false;
      initiallyScrolled.current = assetId;
      return;
    }
    if (messages.length && initiallyScrolled.current !== assetId) {
      node.scrollTop = node.scrollHeight;
      initiallyScrolled.current = assetId;
      stickToBottom.current = true;
      return;
    }
    if (forceBottom.current || stickToBottom.current) {
      node.scrollTop = node.scrollHeight;
      forceBottom.current = false;
    }
  }, [assetId, history?.loading, messages.length, tab]);

  if (assetId === null) return null;
  const title = assetQuery.data?.title ?? 'Ассет';
  const canSend = Boolean(receiverId && (hasRichTextContent(message) || attachments.length) && !uploading);
  const send = () => {
    if (!canSend) return;
    forceBottom.current = true;
    stickToBottom.current = true;
    chat.sendDirect(Number(receiverId), message, assetId, attachments);
    setMessage('');
    setAttachments([]);
  };
  const fileTarget = assetQuery.data?.file_paths && Object.keys(assetQuery.data.file_paths).length
    ? { assetId, title, paths: assetQuery.data.file_paths }
    : null;
  const selectTab = (nextTab: AssetTab) => {
    if (nextTab === tab) return;
    if (nextTab !== 'files' && fileUploadUnfinished.current) { setFileCloseBlocked(true); return; }
    if (boardBusy.current) { setBoardCloseBlocked(true); return; }
    if (tab === 'discussion' && messageList.current) {
      const node = messageList.current;
      discussionScroll.current[assetId] = {
        top: node.scrollTop,
        atBottom: node.scrollHeight - node.clientHeight - node.scrollTop < 48,
      };
    }
    if (nextTab === 'discussion' && tab !== 'discussion') restoreDiscussion.current = true;
    setTab(nextTab);
  };

  return (
    <Modal
      className="asset-details-modal"
      onClose={closeModal}
      size="large"
      title={(
        <>
          {(assetQuery.data?.project?.title || assetQuery.data?.regulation?.title) ? (
            <span className="asset-details__context">
              {assetQuery.data.project?.title ? <span>{assetQuery.data.project.title}</span> : null}
              {assetQuery.data.project?.title && assetQuery.data.regulation?.title ? <ChevronRight aria-hidden="true" size={13} /> : null}
              {assetQuery.data.regulation?.title ? <span>{assetQuery.data.regulation.title}</span> : null}
            </span>
          ) : null}
          <span className="board-title-with-avatar"><span className="asset-details__title">{title}</span>{assetQuery.data ? <BoardAvatarButton kind="asset" id={assetId} title={title}/> : null}</span>
        </>
      )}
      headerToolbar={(
        <div className="asset-details__header-toolbar">
          <nav aria-label="Разделы ассета" className="asset-details__navigation">
            {([
              { id: 'board', label: 'Доска', Icon: SquarePen },
              { id: 'discussion', label: 'Обсуждение', Icon: MessageSquare },
              { id: 'files', label: 'Файлы', Icon: Folder },
            ] as const).map(({ id, label, Icon }) => (
              <button
                aria-pressed={tab === id}
                className="asset-details__tab"
                disabled={id === 'files' && !fileTarget}
                key={id}
                onClick={() => selectTab(id)}
                title={id === 'files' && !fileTarget ? 'У ассета нет файловых папок' : label}
                type="button"
              >
                <span className="asset-details__tab-icon"><Icon aria-hidden="true" size={22} /></span>
                <span>{label}</span>
              </button>
            ))}
          </nav>
          {user?.admin && !user.chatOnly ? <button type="button" className="asset-relations-entry" onClick={() => setRelationsOpen(true)}>Связи ассета</button> : null}
          <span
            className={`asset-details__save-status asset-details__save-status--${boardStatus.kind}`}
            role="status"
            title={boardStatus.message ?? boardStatusLabels[boardStatus.kind]}
          >
            {boardStatus.kind === 'saved' ? <CheckCircle2 aria-hidden="true" size={17} />
              : boardStatus.kind === 'loading' || boardStatus.kind === 'saving' ? <LoaderCircle aria-hidden="true" className="spin" size={17} />
                : <CircleAlert aria-hidden="true" size={17} />}
            <span>{boardStatusLabels[boardStatus.kind]}</span>
          </span>
        </div>
      )}
    >
      {assetQuery.isPending ? <LoadingState /> : null}
      {assetQuery.error ? <ErrorState error={assetQuery.error} retry={() => void assetQuery.refetch()} /> : null}
      {assetQuery.data ? (
        <div className="asset-details">
          {boardCloseBlocked ? (
            <p className="asset-details__save-notice" role="alert">На доске есть несохранённые изменения или идёт загрузка. Дождитесь сохранения; при ошибке повторите его на доске.</p>
          ) : null}

          {tab === 'board' ? <AssetBoard assetId={assetId} key={assetId} onBusyChange={onBoardBusyChange} onStatusChange={setBoardStatus} /> : null}

          {tab === 'discussion' ? (
            <section className="asset-discussion">
              <div
                className="message-list asset-message-list"
                onLoadCapture={(event) => {
                  if (!(event.target instanceof HTMLImageElement) || !stickToBottom.current) return;
                  const node = messageList.current;
                  if (node) node.scrollTop = node.scrollHeight;
                }}
                onScroll={(event) => {
                  const node = event.currentTarget;
                  stickToBottom.current = node.scrollHeight - node.clientHeight - node.scrollTop < 48;
                  discussionScroll.current[assetId] = { top: node.scrollTop, atBottom: stickToBottom.current };
                  if (node.scrollTop > 80 || history?.loading || history?.exhausted || !messages.length) return;
                  historyRestore.current = { assetId, height: node.scrollHeight };
                  chat.loadOlderAssetMessages(assetId);
                }}
                ref={messageList}
              >
                {history?.loading ? <span className="history-loading"><LoaderCircle className="spin" size={15} /> Загружаем предыдущие…</span> : null}
                {messages.map((item, index) => {
                  const author = chat.usersById[item.author_id];
                  const mine = item.author_id === user?.id;
                  return (
                    <Fragment key={item._id}>
                    <MessageDateSeparator createdAt={item.created_at} previousCreatedAt={messages[index - 1]?.created_at} />
                    <article className={`message${mine ? ' message--mine' : ''}`}>
                      {mine ? null : <Avatar size="small" user={author} />}
                      <div className="message__body">
                        {mine ? null : <strong className="message__author">{author?.initial ?? 'Пользователь'}</strong>}
                        <div className="message__bubble">
                          <div className="rich-content" dangerouslySetInnerHTML={{ __html: renderRichText(item.text, item.files) }} />
                          {item.files?.length ? <MessageFiles files={item.files} html={item.text} voiceContext={{eventId:item._id}}/> : null}
                        </div>
                        <time>{formatDate(item.created_at, true)}</time>
                      </div>
                    </article>
                    </Fragment>
                  );
                })}
                {!messages.length && !history?.loading ? <p className="empty-copy">Обсуждение ещё не началось.</p> : null}
              </div>
              <div className="composer asset-composer">
                <PersonCombobox
                  label="Кому"
                  nullLabel="Выберите получателя"
                  onChange={setReceiverId}
                  users={(usersQuery.data ?? []).filter((item) => item.id !== user?.id)}
                  value={receiverId}
                />
                <RichTextEditor
                  minHeight={86}
                  onChange={setMessage}
                  onFiles={(files) => { void attachmentControl.current?.addFiles(files); }}
                  onImageUpload={async (file) => (await attachmentControl.current?.addFiles([file]))?.[0] ?? null}
                  placeholder="Сообщение"
                  value={message}
                />
                <div className="composer__actions">
                  <Attachments
                    attachments={attachments}
                    onChange={(next) => {
                      const removed = attachments.filter((item) => !next.some((kept) => kept.id === item.id)).map((item) => item.id);
                      if (removed.length) setMessage((current) => stripInlineAttachments(current, removed));
                      setAttachments(next);
                    }}
                    onUploadingChange={setUploading}
                    ref={attachmentControl}
                    showTrigger={false}
                  />
                  <button className="button button--primary" disabled={!canSend} onClick={send} type="button">Отправить</button>
                </div>
              </div>
            </section>
          ) : null}

          {tab === 'files' && fileTarget ? <FileBrowser closeBlocked={fileCloseBlocked} key={assetId} onUnfinishedChange={value => { fileUploadUnfinished.current = value; }} target={fileTarget} /> : null}
        </div>
      ) : null}
      {relationsOpen && user?.admin && !user.chatOnly ? <AssetRelationsModal assetId={assetId} onClose={() => setRelationsOpen(false)} /> : null}
    </Modal>
  );
}
